import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Loader2, RefreshCw, Link2 } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { tr } from "@/lib/i18n";
import { trRich } from "@/lib/i18nRich";

function formatError(detail) {
  if (!detail) return tr("Something went wrong. Please try again.");
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail.map((e) => (e?.msg ? e.msg : JSON.stringify(e))).join(" ");
  if (detail?.msg) return detail.msg;
  return String(detail);
}

function timeAgo(iso) {
  if (!iso) return tr("never");
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return tr("never");
  const diff = (Date.now() - d.getTime()) / 1000;
  if (diff < 60) return tr("{floor}s ago", { floor: Math.floor(diff) });
  if (diff < 3600) return tr("{floor}m ago", { floor: Math.floor(diff / 60) });
  if (diff < 86400) return tr("{floor}h ago", { floor: Math.floor(diff / 3600) });
  return tr("{floor}d ago", { floor: Math.floor(diff / 86400) });
}

export default function InternalLinkIndexSettings({
  draft,
  patchDraft,
}) {
  const qc = useQueryClient();
  const { data: status } = useQuery({
    queryKey: ["wp-articles-status"],
    queryFn: api.wpArticlesStatus,
    refetchInterval: false,
    staleTime: 30000,
  });

  const syncMutation = useMutation({
    mutationFn: () => api.syncWpArticles(false),
    onSuccess: (data) => {
      toast.success(
        tr("Indexed {total_in_index} articles ({added} new · {updated} updated) in {took_seconds}s", { total_in_index: data.total_in_index, added: data.added, updated: data.updated, took_seconds: data.took_seconds })
      );
      qc.invalidateQueries({ queryKey: ["wp-articles-status"] });
    },
    onError: (e) =>
      toast.error(formatError(e?.response?.data?.detail) || e.message),
  });

  const incrementalMutation = useMutation({
    mutationFn: () => api.syncWpArticles(true),
    onSuccess: (data) => {
      toast.success(
        tr("Incremental refresh: {added} new · {updated} updated", { added: data.added, updated: data.updated })
      );
      qc.invalidateQueries({ queryKey: ["wp-articles-status"] });
    },
    onError: (e) =>
      toast.error(formatError(e?.response?.data?.detail) || e.message),
  });

  const busy = syncMutation.isPending || incrementalMutation.isPending;

  return (
    <div className="space-y-5">
      <div className="flex items-start gap-3">
        <Link2 className="w-4 h-4 text-[var(--accent)] mt-0.5" />
        <p className="text-xs text-[var(--ink-muted)] leading-relaxed">{trRich("When enabled, every article generated will search this local BM25 index of your past WordPress posts and weave at least <0>{min_internal_links}</0> internal links into the body — improves internal SEO and reader retention. The index runs entirely locally, no external API cost.", { min_internal_links: draft.min_internal_links }, [<span className="font-medium" />])}</p>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        {/* Enabled toggle */}
        <label className="flex items-start gap-2 cursor-pointer">
          <input
            data-testid="settings-internal-links-enabled"
            type="checkbox"
            className="mt-0.5 w-4 h-4 accent-[var(--accent)]"
            checked={draft.internal_links_enabled}
            onChange={(e) =>
              patchDraft({ internal_links_enabled: e.target.checked })
            }
          />
          {trRich("<0><1>Enable internal links</1><2>Turn off to skip the archive lookup at generation time.</2></0>", null, [<span className="text-sm" />, <span className="font-medium" />, <span className="block text-xs text-[var(--ink-muted)]" />])}
        </label>

        {/* Auto-refresh toggle */}
        <label className="flex items-start gap-2 cursor-pointer">
          <input
            data-testid="settings-wp-index-auto-refresh"
            type="checkbox"
            className="mt-0.5 w-4 h-4 accent-[var(--accent)]"
            checked={draft.wp_index_auto_refresh}
            onChange={(e) =>
              patchDraft({ wp_index_auto_refresh: e.target.checked })
            }
          />
          {trRich("<0><1>Auto refresh</1><2>Runs an incremental sync in the background every ~6h while you work.</2></0>", null, [<span className="text-sm" />, <span className="font-medium" />, <span className="block text-xs text-[var(--ink-muted)]" />])}
        </label>

        {/* Min internal links */}
        <div>
          <label
            htmlFor="min-int-links"
            className="overline text-[var(--ink-muted)] block mb-1.5"
          >{tr("Minimum internal links per article")}</label>
          <input
            id="min-int-links"
            data-testid="settings-min-internal-links"
            type="number"
            min={0}
            max={10}
            value={draft.min_internal_links}
            onChange={(e) => {
              const n = parseInt(e.target.value, 10);
              patchDraft({ min_internal_links: Number.isFinite(n) ? n : 0 });
            }}
            className="w-24 border border-[var(--border)] px-3 py-1.5 text-sm text-center focus:outline-none focus:border-[var(--slate-900)]"
          />
          {trRich("<0>0–10. Set to 0 to disable per-generation lookup.</0>", null, [<span className="text-xs text-[var(--ink-muted)] block mt-1" />])}
        </div>

        {/* Index range in years */}
        <div>
          <label
            htmlFor="wp-index-years"
            className="overline text-[var(--ink-muted)] block mb-1.5"
          >{tr("Index range · last N years")}</label>
          <input
            id="wp-index-years"
            data-testid="settings-wp-index-years"
            type="number"
            min={1}
            max={20}
            value={draft.wp_index_years}
            onChange={(e) => {
              const n = parseInt(e.target.value, 10);
              patchDraft({ wp_index_years: Number.isFinite(n) ? n : 3 });
            }}
            className="w-24 border border-[var(--border)] px-3 py-1.5 text-sm text-center focus:outline-none focus:border-[var(--slate-900)]"
          />
          {trRich("<0>Only posts published in the last N years are indexed.</0>", null, [<span className="text-xs text-[var(--ink-muted)] block mt-1" />])}
        </div>
      </div>

      {/* Status + sync */}
      <div className="border-t border-[var(--border)] pt-4">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div className="text-sm">
            <div>
              {trRich("<0>Indexed</0><1>{total_posts}</1> <2>articles</2>", { total_posts: status?.total_posts ?? "—" }, [<span className="overline text-[var(--ink-muted)] mr-2" />, <span
                data-testid="settings-wp-index-count"
                className="font-mono text-base font-medium" />, <span className="text-[var(--ink-muted)] text-xs" />])}
            </div>
            <div className="text-xs text-[var(--ink-muted)] mt-0.5">{tr("Last sync: {timeAgo}", { timeAgo: timeAgo(status?.last_synced_at) })}
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button
              data-testid="settings-wp-index-sync-incremental"
              onClick={() => incrementalMutation.mutate()}
              disabled={busy}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm border border-[var(--border)] hover:border-[var(--ink)] hover:bg-[var(--secondary)] disabled:opacity-60"
              title={tr("Fetch only articles modified since last sync (fast)")}
            >
              {incrementalMutation.isPending ? (
                <Loader2 className="w-3.5 h-3.5 animate-spin" />
              ) : (
                <RefreshCw className="w-3.5 h-3.5" />
              )}{tr("Incremental refresh")}</button>
            <button
              data-testid="settings-wp-index-sync-full"
              onClick={() => syncMutation.mutate()}
              disabled={busy}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm bg-[var(--slate-900)] text-white hover:bg-black disabled:opacity-60"
              title={tr("Re-index every published post in the current range (slower — one-time)")}
            >
              {syncMutation.isPending ? (
                <>
                  <Loader2 className="w-3.5 h-3.5 animate-spin" />{tr("Indexing…")}</>
              ) : (
                <>
                  <RefreshCw className="w-3.5 h-3.5" />{tr("Full re-sync")}</>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
